import React, { useEffect, useState } from 'react';
import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL || 'https://education-crm-2b77a26760ff.herokuapp.com/api';
const NAVY = '#1B2A6B';
const GOLD = '#C9A84C';

const PARTNERSHIP_MODELS = [
  {
    icon: '🎓',
    title: 'Credit Integration Program',
    desc: 'Integrate our AI curriculum into your degree program as elective or core credits. Students earn both college credits and AIAAI certification.',
    benefits: ['Seamless LMS integration', 'Faculty co-certification', 'College branding retained', 'Revenue share model'],
  },
  {
    icon: '📜',
    title: 'Certificate Courses',
    desc: 'Offer standalone AI certificate programs to your students and faculty. No infrastructure changes needed — 100% cloud-based.',
    benefits: ['No infrastructure cost', 'Flexible scheduling', 'Co-branded certificates', 'Industry recognition'],
    highlight: true,
  },
  {
    icon: '💰',
    title: 'Revenue Share Model',
    desc: 'Partner with us and earn per enrolled student. Your institution becomes a Nova Era Labs authorized center with full support.',
    benefits: ['₹5,000+ per enrollment', 'Marketing support provided', 'Student placement priority', 'Dedicated partner manager'],
  },
];

const INST_TYPES = ['Private University', 'Autonomous College', 'Regular College', 'Open University', 'Other'];
const STRENGTHS = ['Under 500', '500–2000', '2000–5000', '5000+'];
const DESIGNATIONS = ['Vice Chancellor', 'Principal', 'Dean', 'HOD', 'Professor', 'Placement Officer', 'Administration', 'Other'];
const INTEREST_OPTIONS = ['Credit Program', 'Certificate Course', 'Revenue Share', 'Full Partnership'];

const STATES = [
  'Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Goa','Gujarat','Haryana',
  'Himachal Pradesh','Jharkhand','Karnataka','Kerala','Madhya Pradesh','Maharashtra','Manipur',
  'Meghalaya','Mizoram','Nagaland','Odisha','Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana',
  'Tripura','Uttar Pradesh','Uttarakhand','West Bengal','Delhi','Jammu and Kashmir','Ladakh',
  'Chandigarh','Puducherry','Other',
];

const inputStyle = {
  width: '100%', padding: '12px 14px', fontSize: 15, border: '1.5px solid #e2e8f0',
  borderRadius: 10, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box', background: '#fff', color: '#1e293b',
};
const labelStyle = { fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 4, display: 'block' };

export default function PartnerWithUsPage() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [interestArea, setInterestArea] = useState([]);
  const [form, setForm] = useState({
    institution_name: '', institution_type: '', city: '', state: '',
    your_name: '', your_designation: '', phone: '', email: '', student_strength: '',
  });

  const urlParams = new URLSearchParams(window.location.search);
  const utmData = {
    utm_source: urlParams.get('utm_source'), utm_medium: urlParams.get('utm_medium'),
    utm_campaign: urlParams.get('utm_campaign'), utm_term: urlParams.get('utm_term'),
    utm_content: urlParams.get('utm_content'),
  };

  useEffect(() => {
    axios.get(`${BASE_URL}/funnel/partner-with-us/track-view`, { params: utmData }).catch(() => {});
  }, []);

  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));
  const toggleInterest = (opt) => setInterestArea(prev =>
    prev.includes(opt) ? prev.filter(x => x !== opt) : [...prev, opt]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.your_name || !form.phone || !form.email) {
      setError('Please fill in your name, phone, and email.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${BASE_URL}/funnel/institutional-inquiry`, {
        ...form, interest_area: interestArea, ...utmData,
      });
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err.response?.data?.message || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) return (
    <div style={{ minHeight: '100vh', background: `linear-gradient(135deg, ${NAVY}, #2d3f8a)`, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ background: '#fff', borderRadius: 20, padding: '48px 36px', textAlign: 'center', maxWidth: 480, width: '100%' }}>
        <div style={{ fontSize: 64, marginBottom: 16 }}>🤝</div>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: NAVY, marginBottom: 10 }}>Inquiry Received!</h2>
        <p style={{ color: '#64748b', fontSize: 15, lineHeight: 1.7 }}>
          Thank you! Our partnership team will contact you within <strong>24 hours</strong> to discuss how we can work together.
        </p>
        <div style={{ marginTop: 20, padding: '16px', background: '#f0fdf4', borderRadius: 12 }}>
          <p style={{ fontSize: 13, color: '#15803d', margin: 0, fontWeight: 600 }}>
            ✅ Your inquiry has been shared with our regional partnership manager.
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', fontFamily: 'Inter, system-ui, sans-serif', background: '#f8fafc' }}>
      <style>{`
        input:focus, select:focus { border-color: ${GOLD} !important; box-shadow: 0 0 0 3px rgba(201,168,76,0.15); }
      `}</style>

      {/* Hero */}
      <div style={{ background: `linear-gradient(135deg, ${NAVY}, #2d3f8a)`, padding: '60px 24px 80px', textAlign: 'center' }}>
        <div style={{ maxWidth: 750, margin: '0 auto' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.4)', borderRadius: 20, padding: '6px 16px', marginBottom: 20 }}>
            <span style={{ color: GOLD, fontSize: 13, fontWeight: 700 }}>🤝 INSTITUTIONAL PARTNERSHIP</span>
          </div>
          <h1 style={{ fontSize: 'clamp(24px, 4.5vw, 42px)', fontWeight: 900, color: '#fff', lineHeight: 1.2, marginBottom: 14 }}>
            Partner with <span style={{ color: GOLD }}>Nova Era Labs</span><br />Bring AI Education to Your Institution
          </h1>
          <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.8)', lineHeight: 1.7, maxWidth: 560, margin: '0 auto 36px' }}>
            Zero infrastructure cost. 100% cloud-based. Industry-certified students. Flexible revenue models.
          </p>

          {/* Key Benefits */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, maxWidth: 680, margin: '0 auto' }}>
            {[
              { icon: '💰', text: 'No Infrastructure Cost' },
              { icon: '☁️', text: '100% Cloud-Based' },
              { icon: '💼', text: 'Internship for Students' },
              { icon: '🏅', text: 'AIAAI Certification' },
            ].map(b => (
              <div key={b.text} style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 12, padding: '14px 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 20 }}>{b.icon}</span>
                <span style={{ fontSize: 13, color: '#fff', fontWeight: 600 }}>{b.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1000, margin: '0 auto', padding: '48px 24px 60px' }}>
        {/* Partnership Models */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <h2 style={{ fontSize: 24, fontWeight: 800, color: NAVY, margin: 0 }}>Partnership Models</h2>
          <p style={{ color: '#64748b', marginTop: 6, fontSize: 14 }}>Choose the model that works best for your institution</p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16, marginBottom: 52 }}>
          {PARTNERSHIP_MODELS.map(m => (
            <div key={m.title} style={{
              background: m.highlight ? `linear-gradient(135deg, ${NAVY}, #2d3f8a)` : '#fff',
              border: m.highlight ? `2px solid ${GOLD}` : '1.5px solid #e2e8f0',
              borderRadius: 16, padding: '24px 22px',
              boxShadow: m.highlight ? '0 8px 30px rgba(27,42,107,0.2)' : 'none',
            }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>{m.icon}</div>
              <h3 style={{ fontSize: 16, fontWeight: 800, color: m.highlight ? '#fff' : NAVY, margin: '0 0 8px' }}>{m.title}</h3>
              <p style={{ fontSize: 13, color: m.highlight ? 'rgba(255,255,255,0.75)' : '#64748b', lineHeight: 1.6, marginBottom: 14 }}>{m.desc}</p>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                {m.benefits.map(b => (
                  <li key={b} style={{ fontSize: 12, color: m.highlight ? 'rgba(255,255,255,0.8)' : '#475569', padding: '4px 0', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ color: m.highlight ? GOLD : '#15803d', fontWeight: 700 }}>✓</span> {b}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Inquiry Form */}
        <div style={{ background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 20, padding: '36px 32px', boxShadow: '0 4px 20px rgba(0,0,0,0.06)', maxWidth: 700, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <h2 style={{ fontSize: 22, fontWeight: 800, color: NAVY, margin: 0 }}>Partnership Inquiry</h2>
            <p style={{ color: '#64748b', marginTop: 6, fontSize: 14 }}>Our team will respond within 24 hours</p>
          </div>

          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gap: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Institution Name</label>
                  <input style={inputStyle} placeholder="College / University name" value={form.institution_name}
                    onChange={e => set('institution_name', e.target.value)} />
                </div>
                <div>
                  <label style={labelStyle}>Institution Type</label>
                  <select style={inputStyle} value={form.institution_type} onChange={e => set('institution_type', e.target.value)}>
                    <option value="">Select type</option>
                    {INST_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>City</label>
                  <input style={inputStyle} placeholder="City" value={form.city} onChange={e => set('city', e.target.value)} />
                </div>
                <div>
                  <label style={labelStyle}>State</label>
                  <select style={inputStyle} value={form.state} onChange={e => set('state', e.target.value)}>
                    <option value="">Select State</option>
                    {STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Your Name *</label>
                  <input style={inputStyle} placeholder="Contact person name" value={form.your_name}
                    onChange={e => set('your_name', e.target.value)} required />
                </div>
                <div>
                  <label style={labelStyle}>Your Designation</label>
                  <select style={inputStyle} value={form.your_designation} onChange={e => set('your_designation', e.target.value)}>
                    <option value="">Select</option>
                    {DESIGNATIONS.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>Phone *</label>
                  <input style={inputStyle} type="tel" placeholder="9876543210" value={form.phone}
                    onChange={e => set('phone', e.target.value)} required />
                </div>
                <div>
                  <label style={labelStyle}>Email *</label>
                  <input style={inputStyle} type="email" placeholder="you@college.edu" value={form.email}
                    onChange={e => set('email', e.target.value)} required />
                </div>
              </div>

              <div>
                <label style={labelStyle}>Student Strength</label>
                <select style={inputStyle} value={form.student_strength} onChange={e => set('student_strength', e.target.value)}>
                  <option value="">Select range</option>
                  {STRENGTHS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>

              {/* Interest Area (multi-select) */}
              <div>
                <label style={labelStyle}>Areas of Interest (select all that apply)</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {INTEREST_OPTIONS.map(opt => (
                    <button key={opt} type="button" onClick={() => toggleInterest(opt)}
                      style={{
                        padding: '7px 14px', borderRadius: 20, border: '1.5px solid',
                        fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
                        background: interestArea.includes(opt) ? NAVY : '#f8fafc',
                        color: interestArea.includes(opt) ? '#fff' : '#475569',
                        borderColor: interestArea.includes(opt) ? NAVY : '#e2e8f0',
                        fontWeight: interestArea.includes(opt) ? 700 : 500,
                      }}>
                      {opt}
                    </button>
                  ))}
                </div>
              </div>

              {error && (
                <div style={{ background: '#fee2e2', color: '#dc2626', padding: '10px 14px', borderRadius: 10, fontSize: 13 }}>{error}</div>
              )}

              <button type="submit" disabled={submitting}
                style={{
                  padding: '15px', background: submitting ? '#94a3b8' : `linear-gradient(135deg, ${NAVY}, #2d3f8a)`,
                  color: '#fff', border: 'none', borderRadius: 12, fontSize: 16, fontWeight: 700,
                  cursor: submitting ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
                }}>
                {submitting ? 'Submitting…' : '🤝 Submit Partnership Inquiry'}
              </button>
              <p style={{ textAlign: 'center', fontSize: 11, color: '#94a3b8', margin: 0 }}>
                Free consultation · No commitment required · Response within 24 hours
              </p>
            </div>
          </form>
        </div>
      </div>

      {/* Footer */}
      <div style={{ background: NAVY, padding: '24px', textAlign: 'center' }}>
        <div style={{ color: GOLD, fontWeight: 800, fontSize: 16, marginBottom: 4 }}>⚡ Nova Era Labs</div>
        <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>Empowering Institutions with AI Education</div>
      </div>
    </div>
  );
}
